import { useMemo, useRef, useState } from "react";
import { FontLibrary as SharedFontLibrary } from "@proof/authoring";
import { fontLibraryServices } from "@proof/sdk";
import type { Project } from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Select, Control } from "./components";
export function FontLibrary(props: {
  project: Project;
  onChange(project: Project): void;
  notify(message: string): void;
  canApply?: boolean;
  hasDocument?: boolean;
}) {
  const { client } = useWorkspaceTransport();
  const [scope, setScope] = useState(
    props.hasDocument === false ? "shared" : "project",
  );
  const latest = useRef(props);
  latest.current = props;
  const source = useMemo(() => fontLibraryServices(client), [client]);
  const services = useMemo(
    () =>
      scope === "shared"
        ? source
        : {
            ...source,
            archiveFont: undefined,
            listFonts: async (signal: AbortSignal) =>
              (await source.listFonts(signal)).filter((record) =>
                props.project.fontManifest.some(
                  (face) => face.id === record.face.id,
                ),
              ),
            ...(source.uploadFont && props.canApply !== false
              ? {
                  uploadFont: async (file: File, signal: AbortSignal) => {
                    const record = await source.uploadFont!(file, signal);
                    const current = latest.current;
                    if (
                      !signal.aborted &&
                      current.project.id === props.project.id &&
                      current.project.tenantId === props.project.tenantId
                    )
                      current.onChange({
                        ...current.project,
                        fontManifest: [
                          ...current.project.fontManifest.filter(
                            (face) => face.id !== record.face.id,
                          ),
                          record.face,
                        ],
                      });
                    return record;
                  },
                }
              : { uploadFont: undefined }),
          },
    [source, scope, props.project, props.canApply],
  );
  return (
    <>
      <Control
        label="Font library scope"
        note={`Project fonts are attached to ${props.project.name}. Choose Shared library to browse the installed baseline and reusable managed faces.`}
      >
        <Select
          value={scope}
          onChange={(event) => setScope(event.target.value)}
        >
          <option value="project" disabled={props.hasDocument === false}>
            This project · {props.project.name}
          </option>
          <option value="shared">Shared library</option>
        </Select>
      </Control>
      <SharedFontLibrary {...props} services={services} />
    </>
  );
}
